import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import { cn } from '@/lib/utils'
const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md text-body font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-(--ui-icon)",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /* Heights come from the density tokens, never a literal — the :root
       * defaults reproduce the old h-9/h-8/h-6/h-10 pixels exactly. */
      size: {
        default: 'h-(--ui-control-h) px-3.5 py-2 has-[>svg]:px-3',
        xs: "h-(--ui-control-h-xs) gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-(--ui-icon-sm)",
        sm: 'h-(--ui-control-h-sm) gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-(--ui-control-h-lg) rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-(--ui-control-h)',
        'icon-xs':
          "size-(--ui-control-h-xs) rounded-md [&_svg:not([class*='size-'])]:size-(--ui-icon-sm)",
        'icon-sm': 'size-(--ui-control-h-sm)',
        'icon-lg': 'size-(--ui-control-h-lg)',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
